import React, { useState } from 'react';
import styles from './AIModelSelectionModal.module.css';
import { AIModel, AI_MODEL_CONFIG } from '../../services/zodiacEngine/AI/Core/AIVariants';
import { useLanguage } from '../Providers/LanguageContext';

// Models available for challenge mode
const IMPLEMENTED_MODELS: AIModel[] = [
  AIModel.SENTINEL,
  AIModel.CREATURE,
  AIModel.ZSTR,
  AIModel.ALCHEMIST,
];

interface AIModelSelectionModalProps {
  isOpen: boolean;
  selectedModel: AIModel | null;
  onSelect: (model: AIModel) => void;
  onClose: () => void;
}

export default function AIModelSelectionModal({
  isOpen,
  selectedModel,
  onSelect,
  onClose,
}: AIModelSelectionModalProps) {
  const { t } = useLanguage();
  const [imageErrors, setImageErrors] = useState<Set<AIModel>>(new Set());

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleModelClick = (model: AIModel) => {
    onSelect(model);
  };

  const handleImageError = (model: AIModel) => {
    setImageErrors(prev => new Set(prev).add(model));
  };

  const isSelected = (model: AIModel) => selectedModel === model;

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <h2 className={styles.modalTitle}>{t('challengeAI.selectAIModel') || 'SELECT AI MODEL'}</h2>

        <div className={styles.modelGrid}>
          {IMPLEMENTED_MODELS.map((model) => {
            const config = AI_MODEL_CONFIG[model];
            const selected = isSelected(model);
            const hasImageError = imageErrors.has(model);

            return (
              <div
                key={model}
                className={`${styles.modelCard} ${selected ? styles.selected : ''}`}
                onClick={() => handleModelClick(model)}
              >
                <div className={styles.modelIconWrapper}>
                  {config.imagePath && !hasImageError ? (
                    <img
                      src={config.imagePath}
                      alt={config.displayName}
                      className={styles.modelIcon}
                      onError={() => handleImageError(model)}
                    />
                  ) : (
                    <div className={styles.modelIconFallback}>
                      {config.displayName.charAt(0)}
                    </div>
                  )}
                </div>
                <div className={styles.modelName}>{config.displayName}</div>
                <div className={styles.modelDescription}>
                  {config.description}
                </div>
              </div>
            );
          })}
        </div>

        <button className={styles.closeButton} onClick={onClose}>
          {t('common.close') || 'Close'}
        </button>
      </div>
    </div>
  );
}
